import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Briefcase, Lock, User, ArrowRight, KeyRound, XCircle, CheckCircle2, AlertCircle, RefreshCw, Mail, Send, ShieldCheck } from 'lucide-react';

const HRLogin: React.FC = () => {
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const navigate = useNavigate();

    // Forgot password state
    const [showForgotModal, setShowForgotModal] = useState(false);
    const [forgotStep, setForgotStep] = useState<1 | 2>(1);
    const [forgotUsername, setForgotUsername] = useState('');
    const [forgotOtp, setForgotOtp] = useState('');
    const [forgotNewPassword, setForgotNewPassword] = useState('');
    const [forgotConfirmPassword, setForgotConfirmPassword] = useState('');
    const [forgotLoading, setForgotLoading] = useState(false);
    const [forgotMsg, setForgotMsg] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

    const handleLogin = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);
        setError('');

        try {
            const response = await fetch('/api/hr/login', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ username, password }),
            });

            const data = await response.json();

            if (response.ok) {
                localStorage.setItem('hrToken', data.token);
                localStorage.setItem('hrUser', data.username);
                navigate('/hr/dashboard');
            } else {
                setError(data.message || data.error || 'Invalid HR credentials');
            }
        } catch (err) {
            setError('Failed to connect to HR server');
        } finally {
            setLoading(false);
        }
    };

    const handleSendOtp = async (e: React.FormEvent) => {
        e.preventDefault();
        setForgotMsg(null);
        if (!forgotUsername.trim()) {
            setForgotMsg({ type: 'error', text: 'Please enter your HR username.' });
            return;
        }

        setForgotLoading(true);
        try {
            const res = await fetch('/api/admin/send-otp', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ username: forgotUsername })
            });

            const data = await res.json();
            if (res.ok) {
                setForgotMsg({ type: 'success', text: data.message || 'OTP sent successfully to IT Admin! Please check inbox.' });
                setForgotStep(2);
            } else {
                setForgotMsg({ type: 'error', text: data.message || 'Failed to send OTP' });
            }
        } catch (err) {
            setForgotMsg({ type: 'error', text: 'Failed to connect to server' });
        } finally {
            setForgotLoading(false);
        }
    };

    const handleResetPasswordWithOtp = async (e: React.FormEvent) => {
        e.preventDefault();
        setForgotMsg(null);

        if (!forgotOtp.trim()) {
            setForgotMsg({ type: 'error', text: 'Please enter the 6-digit OTP code.' });
            return;
        }

        if (forgotNewPassword !== forgotConfirmPassword) {
            setForgotMsg({ type: 'error', text: 'Passwords do not match.' });
            return;
        }

        if (forgotNewPassword.length < 6) {
            setForgotMsg({ type: 'error', text: 'Password must be at least 6 characters.' });
            return;
        }

        setForgotLoading(true);
        try {
            const res = await fetch('/api/admin/reset-password-otp', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    username: forgotUsername,
                    otp: forgotOtp,
                    newPassword: forgotNewPassword
                })
            });

            const data = await res.json();
            if (res.ok) {
                setForgotMsg({ type: 'success', text: data.message || 'Password reset successfully!' });
                setTimeout(() => {
                    setShowForgotModal(false);
                    setForgotMsg(null);
                    setForgotStep(1);
                    setForgotUsername('');
                    setForgotOtp('');
                    setForgotNewPassword('');
                    setForgotConfirmPassword('');
                }, 2000);
            } else {
                setForgotMsg({ type: 'error', text: data.message || 'Failed to reset password' });
            }
        } catch (err) {
            setForgotMsg({ type: 'error', text: 'Failed to connect to server' });
        } finally {
            setForgotLoading(false);
        }
    };

    const resetModalState = () => {
        setShowForgotModal(false);
        setForgotMsg(null);
        setForgotStep(1);
        setForgotUsername('');
        setForgotOtp('');
        setForgotNewPassword('');
        setForgotConfirmPassword('');
    };

    return (
        <div className="min-h-screen flex items-center justify-center bg-[#0f172a] relative overflow-hidden">
            {/* Background Decorative Elements */}
            <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
                <div className="absolute top-[-10%] left-[-10%] w-[40%] h-[40%] bg-blue-600/10 rounded-full blur-[120px]" />
                <div className="absolute bottom-[-10%] right-[-10%] w-[40%] h-[40%] bg-indigo-600/10 rounded-full blur-[120px]" />
            </div>

            <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, ease: "easeOut" }}
                className="max-w-md w-full px-6 z-10"
            >
                <div className="bg-white/5 backdrop-blur-2xl p-10 rounded-[2.5rem] border border-white/10 shadow-[0_25px_50px_-12px_rgba(0,0,0,0.5)]">
                    <div className="text-center mb-10">
                        <motion.div
                            initial={{ scale: 0, rotate: -20 }}
                            animate={{ scale: 1, rotate: 0 }}
                            transition={{
                                type: "spring",
                                stiffness: 260,
                                damping: 20,
                                delay: 0.2
                            }}
                            className="w-24 h-24 bg-gradient-to-tr from-blue-600 to-indigo-600 rounded-3xl flex items-center justify-center mx-auto mb-6 shadow-[0_20px_40px_-10px_rgba(37,99,235,0.4)]"
                        >
                            <Briefcase className="text-white w-12 h-12" />
                        </motion.div>
                        <h1 className="text-4xl font-black text-white mb-2 tracking-tight">HR Portal</h1>
                        <p className="text-blue-200/60 font-medium">Human Resources Management System</p>
                    </div>

                    <form onSubmit={handleLogin} className="space-y-6">
                        {error && (
                            <motion.div
                                initial={{ opacity: 0, scale: 0.95 }}
                                animate={{ opacity: 1, scale: 1 }}
                                className="bg-red-500/10 border border-red-500/20 text-red-400 p-4 rounded-2xl text-sm font-medium flex items-center gap-3"
                            >
                                <div className="w-1.5 h-1.5 bg-red-500 rounded-full animate-pulse" />
                                {error}
                            </motion.div>
                        )}

                        <div className="space-y-2">
                            <label className="text-xs font-bold text-blue-200/40 uppercase tracking-widest ml-1">Username</label>
                            <div className="relative group">
                                <User className="absolute left-4 top-1/2 -translate-y-1/2 text-blue-400 group-focus-within:text-blue-300 transition-colors w-5 h-5" />
                                <input
                                    type="text"
                                    value={username}
                                    onChange={(e) => setUsername(e.target.value)}
                                    className="w-full bg-white/[0.03] border border-white/10 rounded-2xl py-4 pl-12 pr-4 text-white placeholder-white/20 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:bg-white/[0.05] transition-all"
                                    placeholder="HR Administrator"
                                    required
                                />
                            </div>
                        </div>

                        <div className="space-y-2">
                            <div className="flex justify-between items-center ml-1">
                                <label className="text-xs font-bold text-blue-200/40 uppercase tracking-widest">Password</label>
                                <button
                                    type="button"
                                    onClick={() => {
                                        setForgotStep(1);
                                        setForgotMsg(null);
                                        setShowForgotModal(true);
                                    }}
                                    className="text-xs text-blue-400 hover:text-blue-300 transition-colors font-medium"
                                >
                                    Forgot Password?
                                </button>
                            </div>
                            <div className="relative group">
                                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 text-blue-400 group-focus-within:text-blue-300 transition-colors w-5 h-5" />
                                <input
                                    type="password"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    className="w-full bg-white/[0.03] border border-white/10 rounded-2xl py-4 pl-12 pr-4 text-white placeholder-white/20 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:bg-white/[0.05] transition-all"
                                    placeholder="••••••••"
                                    required
                                />
                            </div>
                        </div>

                        <button
                            type="submit"
                            disabled={loading}
                            className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-500 hover:to-indigo-500 text-white font-black py-5 rounded-[1.25rem] shadow-[0_20px_40px_-10px_rgba(37,99,235,0.3)] flex items-center justify-center space-x-3 transition-all transform hover:scale-[1.02] active:scale-[0.98] disabled:opacity-50 group"
                        >
                            <span>{loading ? 'Verifying...' : 'Access Portal'}</span>
                            {!loading && <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />}
                        </button>
                    </form>

                    <div className="mt-10 pt-8 border-t border-white/5 text-center">
                        <p className="text-blue-200/20 text-[10px] font-black uppercase tracking-[0.2em]">
                            Hamdan Trading Group • Secure Infrastructure
                        </p>
                    </div>
                </div>
            </motion.div>

            {/* Forgot Password Modal */}
            <AnimatePresence>
                {showForgotModal && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/80 backdrop-blur-md p-4"
                        onClick={resetModalState}
                    >
                        <motion.div
                            initial={{ scale: 0.9, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            exit={{ scale: 0.9, opacity: 0 }}
                            className="bg-slate-900 border border-white/10 p-8 rounded-3xl max-w-md w-full shadow-2xl relative"
                            onClick={(e) => e.stopPropagation()}
                        >
                            <button
                                onClick={resetModalState}
                                className="absolute top-6 right-6 text-slate-400 hover:text-white"
                            >
                                <XCircle className="w-6 h-6" />
                            </button>

                            <div className="text-center mb-6">
                                <div className="w-14 h-14 bg-blue-600/20 text-blue-400 rounded-2xl flex items-center justify-center mx-auto mb-3">
                                    {forgotStep === 1 ? <User className="w-7 h-7" /> : <ShieldCheck className="w-7 h-7" />}
                                </div>
                                <h3 className="text-xl font-bold text-white">
                                    {forgotStep === 1 ? 'Reset HR Password' : 'Verify OTP & Set Password'}
                                </h3>
                                <p className="text-xs text-slate-400 mt-1">
                                    {forgotStep === 1
                                        ? 'Enter your HR username to request a password reset OTP.'
                                        : `Enter the 6-digit OTP code sent to IT Admin (hamdanit1975@gmail.com) for '${forgotUsername}'`}

                                </p>

                            </div>

                            {forgotMsg && (
                                <div className={`p-4 rounded-xl text-sm font-medium mb-6 flex items-center gap-3 ${
                                    forgotMsg.type === 'success'
                                        ? 'bg-emerald-500/10 border border-emerald-500/20 text-emerald-400'
                                        : 'bg-red-500/10 border border-red-500/20 text-red-400'
                                }`}>
                                    {forgotMsg.type === 'success' ? <CheckCircle2 className="w-5 h-5 shrink-0" /> : <AlertCircle className="w-5 h-5 shrink-0" />}
                                    <span className="text-xs leading-relaxed">{forgotMsg.text}</span>
                                </div>
                            )}

                            {forgotStep === 1 ? (
                                <form onSubmit={handleSendOtp} className="space-y-4">
                                    <div>
                                        <label className="block text-xs font-bold text-slate-400 uppercase tracking-widest mb-1.5">Username</label>
                                        <div className="relative">
                                            <User className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500 w-4 h-4" />
                                            <input
                                                type="text"
                                                value={forgotUsername}
                                                onChange={(e) => setForgotUsername(e.target.value)}
                                                required
                                                className="w-full bg-slate-800 border border-slate-700 rounded-xl pl-11 pr-4 py-3 text-white placeholder-slate-500 focus:outline-none focus:border-blue-500 text-sm"
                                                placeholder="HR Username (e.g. hr_manager)"
                                            />
                                        </div>
                                    </div>

                                    <div className="pt-2 flex gap-3">
                                        <button
                                            type="button"
                                            onClick={resetModalState}
                                            className="w-1/2 py-3 bg-slate-800 hover:bg-slate-700 text-slate-300 font-bold rounded-xl text-sm transition-all"
                                        >
                                            Cancel
                                        </button>
                                        <button
                                            type="submit"
                                            disabled={forgotLoading}
                                            className="w-1/2 py-3 bg-blue-600 hover:bg-blue-500 disabled:opacity-50 text-white font-bold rounded-xl text-sm transition-all shadow-lg shadow-blue-600/30 flex items-center justify-center gap-2"
                                        >
                                            {forgotLoading ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                                            <span>{forgotLoading ? 'Sending...' : 'Send OTP'}</span>
                                        </button>
                                    </div>
                                </form>
                            ) : (
                                <form onSubmit={handleResetPasswordWithOtp} className="space-y-4">
                                    <div>
                                        <label className="block text-xs font-bold text-slate-400 uppercase tracking-widest mb-1.5">6-Digit OTP Code</label>
                                        <div className="relative">
                                            <KeyRound className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500 w-4 h-4" />
                                            <input
                                                type="text"
                                                maxLength={6}
                                                value={forgotOtp}
                                                onChange={(e) => setForgotOtp(e.target.value.replace(/\D/g, ''))}
                                                required
                                                className="w-full bg-slate-800 border border-slate-700 rounded-xl pl-11 pr-4 py-3 text-white placeholder-slate-500 focus:outline-none focus:border-blue-500 text-sm tracking-[0.3em] font-mono"
                                                placeholder="123456"
                                            />
                                        </div>
                                    </div>

                                    <div>
                                        <label className="block text-xs font-bold text-slate-400 uppercase tracking-widest mb-1.5">New Password</label>
                                        <input
                                            type="password"
                                            value={forgotNewPassword}
                                            onChange={(e) => setForgotNewPassword(e.target.value)}
                                            required
                                            className="w-full bg-slate-800 border border-slate-700 rounded-xl px-4 py-3 text-white placeholder-slate-500 focus:outline-none focus:border-blue-500 text-sm"
                                            placeholder="New password (min. 6 chars)"
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-xs font-bold text-slate-400 uppercase tracking-widest mb-1.5">Confirm New Password</label>
                                        <input
                                            type="password"
                                            value={forgotConfirmPassword}
                                            onChange={(e) => setForgotConfirmPassword(e.target.value)}
                                            required
                                            className="w-full bg-slate-800 border border-slate-700 rounded-xl px-4 py-3 text-white placeholder-slate-500 focus:outline-none focus:border-blue-500 text-sm"
                                            placeholder="Confirm new password"
                                        />
                                    </div>

                                    <div className="flex justify-between items-center text-xs pt-1">
                                        <button
                                            type="button"
                                            onClick={() => setForgotStep(1)}
                                            className="text-blue-400 hover:underline"
                                        >
                                            ← Change Username
                                        </button>
                                        <button
                                            type="button"
                                            onClick={handleSendOtp}
                                            disabled={forgotLoading}
                                            className="text-slate-400 hover:text-white"
                                        >
                                            Resend OTP
                                        </button>
                                    </div>

                                    <div className="pt-2 flex gap-3">
                                        <button
                                            type="button"
                                            onClick={resetModalState}
                                            className="w-1/2 py-3 bg-slate-800 hover:bg-slate-700 text-slate-300 font-bold rounded-xl text-sm transition-all"
                                        >
                                            Cancel
                                        </button>
                                        <button
                                            type="submit"
                                            disabled={forgotLoading}
                                            className="w-1/2 py-3 bg-blue-600 hover:bg-blue-500 disabled:opacity-50 text-white font-bold rounded-xl text-sm transition-all shadow-lg shadow-blue-600/30 flex items-center justify-center gap-2"
                                        >
                                            {forgotLoading ? <RefreshCw className="w-4 h-4 animate-spin" /> : null}
                                            <span>{forgotLoading ? 'Resetting...' : 'Reset Password'}</span>
                                        </button>
                                    </div>
                                </form>
                            )}
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};

export default HRLogin;


